import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Button,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
} from "@mui/material";
import {
  Archive,
  ArrowLeft,
  MoreVertical,
  Pencil,
  Pin,
  PinOff,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { PersonalAssistantThread } from "../../constants";

interface PersonalAssistantThreadListProps {
  onBack: () => void;
  threads: PersonalAssistantThread[];
  currentThreadId: string | null;
  onSelectThread: (threadId: string) => void;
  onNewThread: () => void;
  onRefresh: () => void;
  onPinThread: (threadId: string, isPinned: boolean) => void;
  onArchiveThread: (threadId: string) => void;
  onDeleteThread: (threadId: string) => void;
  onRenameThread: (threadId: string, title: string) => void;
  loading?: boolean;
}

const PersonalAssistantThreadList: React.FC<PersonalAssistantThreadListProps> = ({
  onBack,
  threads,
  currentThreadId,
  onSelectThread,
  onNewThread,
  onRefresh,
  onPinThread,
  onArchiveThread,
  onDeleteThread,
  onRenameThread,
  loading = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [renamingThread, setRenamingThread] = React.useState<PersonalAssistantThread | null>(null);
  const [renameValue, setRenameValue] = React.useState("");

  const openRenameDialog = (thread: PersonalAssistantThread) => {
    setRenamingThread(thread);
    setRenameValue(thread.title || "");
  };

  const closeRenameDialog = () => {
    setRenamingThread(null);
    setRenameValue("");
  };

  const submitRename = () => {
    if (!renamingThread) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === renamingThread.title) {
      closeRenameDialog();
      return;
    }
    onRenameThread(renamingThread.id, trimmed);
    closeRenameDialog();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  const handleSelectThread = (threadId: string) => {
    onSelectThread(threadId);
    onBack();
  };

  const handleNewThread = () => {
    onNewThread();
    onBack();
  };

  const pinnedThreads = threads.filter((t) => t.isPinned);
  const regularThreads = threads.filter((t) => !t.isPinned);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          backgroundColor: isDark ? "#1a1a1a" : "#f5f5f5",
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" onClick={onBack}>
            <ArrowLeft size={18} />
          </IconButton>
          <Typography variant="h6" fontWeight={600}>
            Conversations
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" onClick={onRefresh} disabled={loading}>
            <RefreshCw size={18} />
          </IconButton>
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={handleNewThread}
          >
            New
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", backgroundColor: theme.palette.background.default }}>
        {threads.length === 0 && !loading && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No conversations yet.
              <br />
              Start a new one!
            </Typography>
          </Box>
        )}

        {/* Pinned threads */}
        {pinnedThreads.length > 0 && (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 2, pt: 2, display: "block" }}
            >
              Pinned
            </Typography>
            <List dense disablePadding>
              {pinnedThreads.map((thread) => (
                <ThreadListItem
                  key={thread.id}
                  thread={thread}
                  isSelected={thread.id === currentThreadId}
                  onSelect={handleSelectThread}
                  onPin={onPinThread}
                  onArchive={onArchiveThread}
                  onDelete={onDeleteThread}
                  onRename={openRenameDialog}
                  formatDate={formatDate}
                />
              ))}
            </List>
            <Divider sx={{ my: 1 }} />
          </>
        )}

        {/* Regular threads */}
        {regularThreads.length > 0 && (
          <List dense disablePadding>
            {regularThreads.map((thread) => (
              <ThreadListItem
                key={thread.id}
                thread={thread}
                isSelected={thread.id === currentThreadId}
                onSelect={handleSelectThread}
                onPin={onPinThread}
                onArchive={onArchiveThread}
                onDelete={onDeleteThread}
                onRename={openRenameDialog}
                formatDate={formatDate}
              />
            ))}
          </List>
        )}
      </Box>

      <Dialog open={renamingThread !== null} onClose={closeRenameDialog} fullWidth maxWidth="xs">
        <DialogTitle>Rename conversation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Title"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitRename();
              }
            }}
            inputProps={{ maxLength: 200 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenameDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitRename} disabled={!renameValue.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface ThreadListItemProps {
  thread: PersonalAssistantThread;
  isSelected: boolean;
  onSelect: (threadId: string) => void;
  onPin: (threadId: string, isPinned: boolean) => void;
  onArchive: (threadId: string) => void;
  onDelete: (threadId: string) => void;
  onRename: (thread: PersonalAssistantThread) => void;
  formatDate: (dateStr: string | null) => string;
}

const ThreadListItem: React.FC<ThreadListItemProps> = ({
  thread,
  isSelected,
  onSelect,
  onPin,
  onArchive,
  onDelete,
  onRename,
  formatDate,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <ListItem disablePadding>
      <ListItemButton
        selected={isSelected}
        onClick={() => onSelect(thread.id)}
      >
        <ListItemText
          primary={thread.title || "New conversation"}
          secondary={formatDate(thread.lastMessageAt)}
          primaryTypographyProps={{
            noWrap: true,
            variant: "body2",
            fontWeight: isSelected ? 600 : 400,
          }}
          secondaryTypographyProps={{
            variant: "caption",
          }}
        />
        <IconButton
          size="small"
          onClick={handleMenuClick}
          sx={{ ml: 1 }}
        >
          <MoreVertical size={16} />
        </IconButton>
      </ListItemButton>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            onRename(thread);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Pencil size={16} />
          </ListItemIcon>
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            onPin(thread.id, !thread.isPinned);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            {thread.isPinned ? <PinOff size={16} /> : <Pin size={16} />}
          </ListItemIcon>
          {thread.isPinned ? "Unpin" : "Pin"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            onArchive(thread.id);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Archive size={16} />
          </ListItemIcon>
          Archive
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDelete(thread.id);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Trash2 size={16} />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </ListItem>
  );
};

export default PersonalAssistantThreadList;
